// Role-Based Access Control (RBAC) definitions for ChenaiXR EndoNurseVR LMS

/**
 * User roles in the system
 */
export const ROLES = {
  ADMINISTRATOR: 'Administrator',
  INSTRUCTOR: 'Instructor',
  TRAINEE: 'Trainee'
};

/**
 * System permissions
 */
export const PERMISSIONS = {
  // Content Library permissions
  VIEW_ALL_CONTENT: 'view_all_content',
  VIEW_ENROLLED_CONTENT: 'view_enrolled_content',
  UPLOAD_CONTENT: 'upload_content',

  // Explainer Videos permissions
  VIEW_ALL_EXPLAINER_VIDEOS: 'view_all_explainer_videos',
  VIEW_ENROLLED_EXPLAINER_VIDEOS: 'view_enrolled_explainer_videos',
  UPLOAD_EXPLAINER_VIDEOS: 'upload_explainer_videos',
  EDIT_EXPLAINER_VIDEOS: 'edit_explainer_videos',
  DELETE_EXPLAINER_VIDEOS: 'delete_explainer_videos',

  // Module permissions
  CREATE_MODULES: 'create_modules',
  EDIT_ALL_MODULES: 'edit_all_modules',
  EDIT_ASSIGNED_MODULES: 'edit_assigned_modules',
  VIEW_ALL_MODULES: 'view_all_modules',
  VIEW_ENROLLED_MODULES: 'view_enrolled_modules',
  DELETE_MODULES: 'delete_modules',

  // User management permissions
  MANAGE_ALL_USERS: 'manage_all_users',
  ENROLL_TRAINEES: 'enroll_trainees',
  VIEW_ALL_USERS: 'view_all_users',
  VIEW_OWN_PROFILE: 'view_own_profile',

  // Analytics permissions
  VIEW_SYSTEM_ANALYTICS: 'view_system_analytics',
  VIEW_MODULE_ANALYTICS: 'view_module_analytics',
  VIEW_TRAINEE_ANALYTICS: 'view_trainee_analytics',
  VIEW_PERSONAL_ANALYTICS: 'view_personal_analytics',

  // System permissions
  ACCESS_SETTINGS: 'access_settings',
  ACCESS_ALL_ROUTES: 'access_all_routes',

  // VR Access (Meta Developer Hub release channels)
  MANAGE_VR_ACCESS: 'manage_vr_access',

  // Virtual Tours (curated web-based virtual tours)
  VIEW_VIRTUAL_TOURS: 'view_virtual_tours',
  MANAGE_VIRTUAL_TOURS: 'manage_virtual_tours',
};

/**
 * Role to permissions mapping
 */
export const ROLE_PERMISSIONS = {
  [ROLES.ADMINISTRATOR]: [
    PERMISSIONS.VIEW_ALL_CONTENT,
    PERMISSIONS.UPLOAD_CONTENT,
    PERMISSIONS.CREATE_MODULES,
    PERMISSIONS.EDIT_ALL_MODULES,
    PERMISSIONS.VIEW_ALL_MODULES,
    PERMISSIONS.DELETE_MODULES,
    PERMISSIONS.MANAGE_ALL_USERS,
    PERMISSIONS.VIEW_ALL_USERS,
    PERMISSIONS.VIEW_OWN_PROFILE,
    PERMISSIONS.ENROLL_TRAINEES,
    PERMISSIONS.VIEW_SYSTEM_ANALYTICS,
    PERMISSIONS.VIEW_MODULE_ANALYTICS,
    PERMISSIONS.VIEW_TRAINEE_ANALYTICS,
    PERMISSIONS.ACCESS_SETTINGS,
    PERMISSIONS.ACCESS_ALL_ROUTES,
    PERMISSIONS.VIEW_ALL_EXPLAINER_VIDEOS,
    PERMISSIONS.VIEW_ENROLLED_EXPLAINER_VIDEOS,
    PERMISSIONS.UPLOAD_EXPLAINER_VIDEOS,
    PERMISSIONS.EDIT_EXPLAINER_VIDEOS,
    PERMISSIONS.DELETE_EXPLAINER_VIDEOS,
    PERMISSIONS.MANAGE_VR_ACCESS,
    PERMISSIONS.VIEW_VIRTUAL_TOURS,
    PERMISSIONS.MANAGE_VIRTUAL_TOURS,
  ],

  [ROLES.INSTRUCTOR]: [
    PERMISSIONS.VIEW_ALL_CONTENT,
    PERMISSIONS.CREATE_MODULES,
    PERMISSIONS.EDIT_ASSIGNED_MODULES,
    PERMISSIONS.VIEW_ALL_MODULES,
    PERMISSIONS.VIEW_ALL_USERS,
    PERMISSIONS.VIEW_OWN_PROFILE,
    PERMISSIONS.ENROLL_TRAINEES,
    PERMISSIONS.VIEW_MODULE_ANALYTICS,
    PERMISSIONS.VIEW_TRAINEE_ANALYTICS,
    PERMISSIONS.ACCESS_SETTINGS,
    PERMISSIONS.VIEW_ALL_EXPLAINER_VIDEOS,
    PERMISSIONS.VIEW_VIRTUAL_TOURS,
  ],

  [ROLES.TRAINEE]: [
    PERMISSIONS.VIEW_ENROLLED_CONTENT,
    PERMISSIONS.VIEW_ENROLLED_MODULES,
    PERMISSIONS.VIEW_OWN_PROFILE,
    PERMISSIONS.VIEW_PERSONAL_ANALYTICS,
    PERMISSIONS.VIEW_ENROLLED_EXPLAINER_VIDEOS,
    PERMISSIONS.VIEW_VIRTUAL_TOURS,
  ],
};

/**
 * Check if a user has a specific permission
 * @param {Object} user - User object with role property
 * @param {string} permission - Permission to check
 * @returns {boolean} True if user has the permission
 */
export const hasPermission = (user, permission) => {
  if (!user || !user.role) return false;
  const userPermissions = ROLE_PERMISSIONS[user.role] || [];
  return userPermissions.includes(permission);
};

/**
 * Check if a user can access a specific route
 * @param {Object} user - User object with role property
 * @param {string} route - Route path (e.g., '/modules', '/enrollment')
 * @returns {boolean} True if user can access the route
 */
export const canAccessRoute = (user, route) => {
  if (!user || !user.role) return false;

  // Block Trainees from accessing Content Library list, but allow content details via enrolled modules
  if (user.role === ROLES.TRAINEE && route === '/content-library') return false;
  // Block Trainees from accessing Explainer Videos list, but allow video details via enrolled modules
  if (user.role === ROLES.TRAINEE && route === '/explainer-videos') return false;

  // Route to required permissions mapping
  const routePermissions = {
    '/': [], // Dashboard - everyone can access
    '/content-library': [PERMISSIONS.VIEW_ALL_CONTENT, PERMISSIONS.VIEW_ENROLLED_CONTENT],
    '/content-library/:id': [PERMISSIONS.VIEW_ALL_CONTENT, PERMISSIONS.VIEW_ENROLLED_CONTENT],
    '/modules': [PERMISSIONS.CREATE_MODULES, PERMISSIONS.EDIT_ALL_MODULES, PERMISSIONS.EDIT_ASSIGNED_MODULES, PERMISSIONS.VIEW_ALL_MODULES, PERMISSIONS.VIEW_ENROLLED_MODULES],
    '/modules/:id': [PERMISSIONS.CREATE_MODULES, PERMISSIONS.EDIT_ALL_MODULES, PERMISSIONS.EDIT_ASSIGNED_MODULES, PERMISSIONS.VIEW_ALL_MODULES, PERMISSIONS.VIEW_ENROLLED_MODULES],
    '/enrollment': [PERMISSIONS.MANAGE_ALL_USERS, PERMISSIONS.ENROLL_TRAINEES, PERMISSIONS.VIEW_ALL_USERS],
    '/profile': [PERMISSIONS.VIEW_OWN_PROFILE],
    '/settings': [PERMISSIONS.ACCESS_SETTINGS],
    '/analytics': [PERMISSIONS.VIEW_SYSTEM_ANALYTICS, PERMISSIONS.VIEW_MODULE_ANALYTICS, PERMISSIONS.VIEW_PERSONAL_ANALYTICS],
    '/analytics/module/:id': [PERMISSIONS.VIEW_SYSTEM_ANALYTICS, PERMISSIONS.VIEW_MODULE_ANALYTICS],
    '/trainees/:username': [PERMISSIONS.VIEW_TRAINEE_ANALYTICS],
    '/explainer-videos': [PERMISSIONS.VIEW_ALL_EXPLAINER_VIDEOS, PERMISSIONS.VIEW_ENROLLED_EXPLAINER_VIDEOS],
    '/explainer-videos/:id': [PERMISSIONS.VIEW_ALL_EXPLAINER_VIDEOS, PERMISSIONS.VIEW_ENROLLED_EXPLAINER_VIDEOS],
    '/vr-access': [PERMISSIONS.MANAGE_VR_ACCESS],
    '/vr-access/channels': [PERMISSIONS.MANAGE_VR_ACCESS],
    '/virtual-tours': [PERMISSIONS.VIEW_VIRTUAL_TOURS, PERMISSIONS.MANAGE_VIRTUAL_TOURS],
    '/virtual-tours/:id': [PERMISSIONS.VIEW_VIRTUAL_TOURS, PERMISSIONS.MANAGE_VIRTUAL_TOURS],
  };

  const requiredPermissions = routePermissions[route] || [];

  // If no specific permissions required for this route, allow access
  if (requiredPermissions.length === 0) return true;

  // Check if user has any of the required permissions
  return requiredPermissions.some(permission => hasPermission(user, permission));
};

/**
 * Get all permissions for a specific role
 * @param {string} role - Role name
 * @returns {Array} Array of permissions for the role
 */
export const getPermissionsForRole = (role) => {
  return ROLE_PERMISSIONS[role] || [];
};

/**
 * Check if user can perform an action on a module
 * @param {Object} user - User object
 * @param {Object} module - Module object
 * @param {string} action - Action to perform ('view', 'edit', 'delete')
 * @returns {boolean} True if user can perform the action
 */
export const canPerformModuleAction = (user, module, action = 'view') => {
  if (!user || !user.role) return false;

  if (user.role === ROLES.ADMINISTRATOR) return true;

  if (user.role === ROLES.INSTRUCTOR) {
    if (action === 'view') {
      const isCreatorOrAssigned = (module.createdBy && module.createdBy === user.id) ||
        (module.instructorIds && module.instructorIds.includes(user.id));
      return isCreatorOrAssigned && hasPermission(user, PERMISSIONS.VIEW_ALL_MODULES);
    }
    if (action === 'edit') {
      // Instructor can edit modules they created or are assigned to
      const isCreatorOrAssigned = (module.createdBy && module.createdBy === user.id) ||
        (module.instructorIds && module.instructorIds.includes(user.id));
      return isCreatorOrAssigned && hasPermission(user, PERMISSIONS.EDIT_ASSIGNED_MODULES);
    }
    if (action === 'delete') {
      return false; // Instructors cannot delete modules
    }
    if (action === 'create') {
      return hasPermission(user, PERMISSIONS.CREATE_MODULES);
    }
  }

  if (user.role === ROLES.TRAINEE) {
    if (action === 'view') {
      // module.traineeIds is an array from the API; user.enrolledModules is a count.
      const isEnrolled = (Array.isArray(module.traineeIds) && module.traineeIds.includes(user.id)) ||
        (Array.isArray(user.enrolledModules) && user.enrolledModules.includes(module.id));
      return isEnrolled && hasPermission(user, PERMISSIONS.VIEW_ENROLLED_MODULES);
    }
    // Trainees cannot edit, delete, or create modules
    return false;
  }

  return false;
};

/**
 * Check if user can upload content
 * @param {Object} user - User object
 * @returns {boolean} True if user can upload content
 */
export const canUploadContent = (user) => {
  return hasPermission(user, PERMISSIONS.UPLOAD_CONTENT);
};

/**
 * Check if user can upload explainer videos
 * @param {Object} user - User object
 * @returns {boolean} True if user can upload explainer videos
 */
export const canUploadExplainerVideos = (user) => {
  return hasPermission(user, PERMISSIONS.UPLOAD_EXPLAINER_VIDEOS);
};

/**
 * Check if user can enroll trainees
 * @param {Object} user - User object
 * @returns {boolean} True if user can enroll trainees
 */
export const canEnrollTrainees = (user) => {
  return hasPermission(user, PERMISSIONS.ENROLL_TRAINEES);
};